import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceProviderListItem } from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceOperationsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type ProviderSortField = 'code' | 'name' | 'providerType' | 'contractStatus';

@Component({
  standalone: true,
  selector: 'ain-insurance-health-providers-shell',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    FormsModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.health.providers.title')"
      [description]="t('insurance.health.providers.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.health.providers.help.page.title'"
        [descriptionKey]="'insurance.health.providers.help.page.description'"
        [bulletKeys]="[
          'insurance.health.providers.help.page.bullets.scope',
          'insurance.health.providers.help.page.bullets.create',
          'insurance.health.providers.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.health.providers.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.health.providers.help.listFilters.title'"
                [descriptionKey]="'insurance.health.providers.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.health.providers.help.listFilters.bullets.search',
                  'insurance.health.providers.help.listFilters.bullets.providerType',
                  'insurance.health.providers.help.listFilters.bullets.contractStatus'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.health.providers.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.health.providers.fields.providerType') }}</span>
                <p-select
                  [options]="providerTypeOptions"
                  optionValue="value"
                  [(ngModel)]="providerTypeFilter"
                  name="providerTypeFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.health.providers.fields.contractStatus') }}</span>
                <p-select
                  [options]="contractStatusOptions"
                  optionValue="value"
                  [(ngModel)]="contractStatusFilter"
                  name="contractStatusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.health.providers.states.empty')" [message]="t('insurance.health.providers.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('code')">{{ t('insurance.health.providers.fields.code') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('name')">{{ t('insurance.health.providers.fields.name') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('providerType')">{{ t('insurance.health.providers.fields.providerType') }}</button></th>
                  <th>{{ t('insurance.health.providers.fields.networkTier') }}</th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('contractStatus')">{{ t('insurance.health.providers.fields.contractStatus') }}</button></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.code }}</button></td>
                  <td>{{ row.name }}</td>
                  <td>{{ t(providerTypeKey(row.providerType)) }}</td>
                  <td>{{ t(networkTierKey(row.networkTier)) }}</td>
                  <td>{{ t(contractStatusKey(row.contractStatus)) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceHealthProvidersShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly info = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceProviderListItem[]>([]);
  readonly selectedRows = signal<InsuranceProviderListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<ProviderSortField>('code');
  readonly sortOrder = signal<SortOrder>(1);
  providerTypeFilter: string | null = null;
  contractStatusFilter: string | null = null;

  liveStatusKey = liveStatusKey;

  readonly providerTypeOptions = [
    { value: 'Hospital', labelKey: 'insurance.health.providers.enums.providerType.hospital' },
    { value: 'Clinic', labelKey: 'insurance.health.providers.enums.providerType.clinic' },
    { value: 'Lab', labelKey: 'insurance.health.providers.enums.providerType.lab' },
    { value: 'Pharmacy', labelKey: 'insurance.health.providers.enums.providerType.pharmacy' },
    { value: 'Physician', labelKey: 'insurance.health.providers.enums.providerType.physician' }
  ];

  readonly contractStatusOptions = [
    { value: 'Active', labelKey: 'insurance.health.providers.enums.contractStatus.active' },
    { value: 'Suspended', labelKey: 'insurance.health.providers.enums.contractStatus.suspended' },
    { value: 'Expired', labelKey: 'insurance.health.providers.enums.contractStatus.expired' }
  ];

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listHealthProviders().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.info.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceProviderListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = this.rows()
      .filter(row => !value || row.code.toLowerCase().includes(value) || row.name.toLowerCase().includes(value))
      .filter(row => !this.providerTypeFilter || row.providerType === this.providerTypeFilter)
      .filter(row => !this.contractStatusFilter || row.contractStatus === this.contractStatusFilter);

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsuranceProviderListItem[] | InsuranceProviderListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/health/providers', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/health/providers/new']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-health-providers.csv',
      ['Code', 'Name', 'ProviderType', 'NetworkTier', 'ContractStatus'],
      rows.map(item => [
        item.code,
        item.name,
        item.providerType,
        item.networkTier,
        item.contractStatus
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: ProviderSortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  providerTypeKey(value: string): string {
    return `insurance.health.providers.enums.providerType.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
  }

  networkTierKey(value: string): string {
    return `insurance.health.providers.enums.networkTier.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
  }

  contractStatusKey(value: string): string {
    return `insurance.health.providers.enums.contractStatus.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceOperationsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
